import React from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { ChevronRight, ArrowLeft, Layers, GripVertical, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Hierarchy editor — extracted from SemanticModelsPage.
 * Drag-and-drop dimension fields into ordered drill levels for a single
 * entity, then save the hierarchy back via `api.updateSemanticEntity`.
 */
export function HierarchyEditorDialog({
  open, onOpenChange,
  selectedEntityData,
  entities, setEntities,
  editHierarchyIdx,
  hierarchyName, setHierarchyName,
  hierarchyLevels, setHierarchyLevels,
}) {
  const [fieldSearch, setFieldSearch] = React.useState('');
  const availableFields = (selectedEntityData?.attributes || [])
    .filter(a => a.semantic_type === 'dimension' && hierarchyLevels.indexOf(a.name) < 0)
    .filter(a => {
      const q = fieldSearch.trim().toLowerCase();
      if (!q) return true;
      return [a.business_name, a.display_name, a.name, a.description]
        .some(v => (v || '').toLowerCase().includes(q));
    });

  const moveLevel = (idx, dir) => {
    setHierarchyLevels(prev => {
      const next = prev.slice();
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      const tmp = next[idx]; next[idx] = next[swap]; next[swap] = tmp;
      return next;
    });
  };

  const removeLevel = (idx) => setHierarchyLevels(prev => prev.filter((_, i) => i !== idx));

  // Starter-hierarchy templates — detect ordered Time / Geography levels
  // from the entity's dimension fields and offer one-click builds.
  const dims = (selectedEntityData?.attributes || []).filter(a => a.semantic_type === 'dimension');
  const findLevel = (keywords) => {
    for (const kw of keywords) {
      const hit = dims.find(a => [a.business_name, a.display_name, a.name]
        .some(v => (v || '').toLowerCase().includes(kw)));
      if (hit) return hit.name;
    }
    return null;
  };
  const buildTemplate = (levelKeywordGroups) => {
    const names = [];
    for (const group of levelKeywordGroups) {
      const n = findLevel(group);
      if (n && names.indexOf(n) < 0) names.push(n);
    }
    return names;
  };
  const templates = [
    { key: 'time', label: 'Time', levels: buildTemplate([
      ['year', 'año', 'ano', 'année', 'jahr', 'anno'],
      ['quarter', 'trimestre', 'quartal'],
      ['month', 'mes', 'mês', 'mois', 'monat', 'mese'],
      ['week', 'semana', 'semaine', 'woche', 'settimana'],
      ['day of', 'date', 'día', 'dia', 'jour', 'tag', 'giorno', 'fecha', 'data'],
    ]) },
    { key: 'geography', label: 'Geography', levels: buildTemplate([
      ['country', 'país', 'pais', 'pays', 'land', 'paese'],
      ['region', 'state', 'province', 'región', 'region', 'estado', 'provincia', 'région', 'bundesland'],
      ['county', 'condado', 'comté'],
      ['city', 'metro', 'ciudad', 'cidade', 'ville', 'stadt', 'città'],
      ['postal', 'zip', 'código postal', 'codigo postal', 'plz', 'cap'],
    ]) },
    { key: 'product', label: 'Product', levels: buildTemplate([
      ['category', 'categoría', 'categoria', 'catégorie', 'kategorie'],
      ['subcategory', 'sub category', 'sub-category', 'subcategoría', 'subcategoria', 'sous-catégorie'],
      ['brand', 'marca', 'marque', 'marke'],
      ['product', 'sku', 'item', 'producto', 'produto', 'produit', 'produkt', 'artículo', 'articolo'],
    ]) },
    { key: 'org', label: 'Org Chart', levels: buildTemplate([
      ['division', 'división', 'divisao', 'divisão'],
      ['department', 'dept', 'departamento', 'département', 'abteilung'],
      ['team', 'group', 'equipo', 'equipe', 'équipe', 'gruppe'],
      ['manager', 'supervisor', 'gerente', 'responsable', 'leiter'],
      ['employee', 'staff', 'agent', 'empleado', 'funcionário', 'employé', 'mitarbeiter'],
    ]) },
  ].filter(t => t.levels.length >= 2);

  const applyTemplate = (t) => {
    if (!hierarchyName.trim()) setHierarchyName(t.label);
    setHierarchyLevels(t.levels);
  };

  const save = async () => {
    if (!hierarchyName.trim() || hierarchyLevels.length === 0) {
      toast.error('Name and at least one level required');
      return;
    }
    const en = entities.find(e => e.entity_id === selectedEntityData?.entity_id);
    if (!en) return;
    const newEntry = { name: hierarchyName, levels: hierarchyLevels };
    const nh = editHierarchyIdx !== null
      ? (en.hierarchies || []).map((hh, i) => i === editHierarchyIdx ? newEntry : hh)
      : (en.hierarchies || []).concat([newEntry]);
    try {
      const updated = await api.updateSemanticEntity(selectedEntityData.entity_id, { hierarchies: nh });
      setEntities(prev => prev.map(e => e.entity_id === selectedEntityData.entity_id ? updated : e));
      onOpenChange(false);
      toast.success(editHierarchyIdx !== null ? 'Hierarchy updated' : 'Hierarchy created');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading">{editHierarchyIdx !== null ? 'Edit Hierarchy' : 'Create Hierarchy'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <Label className="text-[10px] text-muted-foreground">Hierarchy Name</Label>
            <Input
              value={hierarchyName}
              onChange={e => setHierarchyName(e.target.value)}
              placeholder="e.g. Time, Geography, Product"
              className="rounded-lg bg-muted/50 border-border/50 mt-0.5"
              data-testid="hierarchy-name-input"
            />
          </div>
          {editHierarchyIdx === null && hierarchyLevels.length === 0 && templates.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="hierarchy-templates">
              <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Quick start</span>
              {templates.map(t => (
                <button
                  key={t.key}
                  onClick={() => applyTemplate(t)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 transition-colors"
                  data-testid={`hierarchy-template-${t.key}`}
                >
                  <Layers className="w-3 h-3" /> {t.label} ({t.levels.length})
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3" style={{ minHeight: 240 }}>
            <div>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1.5">Available Fields</p>
              <Input
                value={fieldSearch}
                onChange={e => setFieldSearch(e.target.value)}
                placeholder="Search fields…"
                className="h-7 !text-[10px] rounded-lg bg-muted/40 border-border/40 mb-1.5"
                data-testid="hierarchy-field-search"
              />
              <p className="text-xs text-muted-foreground/70 mb-1">Drag fields to the hierarchy levels on the right</p>
              <div className="rounded-lg border border-border/30 max-h-52 overflow-auto">
                {availableFields.map(a => (
                  <div
                    key={a.name}
                    draggable
                    onDragStart={e => { e.dataTransfer.setData('text/plain', a.name); e.dataTransfer.effectAllowed = 'copy'; }}
                    className="flex items-center gap-1.5 px-2 py-1.5 cursor-grab active:cursor-grabbing hover:bg-emerald-500/10 border-b border-border/10 last:border-0 transition-colors"
                    data-testid={`hier-field-${a.name}`}
                  >
                    <GripVertical className="w-3 h-3 text-muted-foreground/30" />
                    <Layers className="w-3 h-3 text-emerald-400" />
                    <span className="text-[10px] text-foreground truncate">{a.business_name || a.display_name || a.name}</span>
                    <span className="text-[8px] font-mono text-muted-foreground/70 ml-auto">{a.name}</span>
                  </div>
                ))}
                {availableFields.length === 0 && (
                  <p className="text-[9px] text-muted-foreground/70 text-center py-4">{fieldSearch.trim() ? 'No fields match your search' : 'All dimensions added'}</p>
                )}
              </div>
            </div>
            <div className="min-w-0">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1.5">Hierarchy Levels (top to bottom)</p>
              <div
                className="rounded-lg border-2 border-dashed border-border/30 min-h-[200px] p-2 overflow-hidden"
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-primary/50', 'bg-primary/5'); }}
                onDragLeave={e => e.currentTarget.classList.remove('border-primary/50', 'bg-primary/5')}
                onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-primary/50', 'bg-primary/5');
                  const fieldName = e.dataTransfer.getData('text/plain');
                  if (fieldName && hierarchyLevels.indexOf(fieldName) < 0) {
                    setHierarchyLevels(prev => prev.concat([fieldName]));
                  }
                }}
                data-testid="hierarchy-drop-zone"
              >
                {hierarchyLevels.length === 0 && (
                  <p className="text-[9px] text-muted-foreground/70 text-center py-8">Drop dimension fields here to build the drill path</p>
                )}
                {hierarchyLevels.map((level, li) => {
                  const fieldAttr = (selectedEntityData?.attributes || []).find(a => a.name === level);
                  return (
                    <div key={level} className="flex items-center gap-1.5 mb-1 group/lv min-w-0" style={{ marginLeft: Math.min(li, 4) * 14 }}>
                      <div className="flex items-center gap-1 flex-1 min-w-0 p-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <span className="text-[8px] text-emerald-400/60 font-mono w-4 text-center flex-shrink-0">{li + 1}</span>
                        <ChevronRight className="w-2.5 h-2.5 text-emerald-400/40 flex-shrink-0" />
                        <span className="text-[10px] text-foreground truncate flex-1 min-w-0">{fieldAttr?.business_name || fieldAttr?.display_name || level}</span>
                        <span className="text-[9px] font-mono text-muted-foreground/70 flex-shrink-0 max-w-[72px] truncate hidden sm:inline">{level}</span>
                      </div>
                      <div className="flex gap-0.5 flex-shrink-0 opacity-70 group-hover/lv:opacity-100 transition-opacity">
                        {li > 0 && <button onClick={() => moveLevel(li, -1)} className="text-muted-foreground/70 hover:text-foreground" title="Move up" data-testid={`level-up-${li}`}><ArrowLeft className="w-3 h-3 rotate-90" /></button>}
                        {li < hierarchyLevels.length - 1 && <button onClick={() => moveLevel(li, 1)} className="text-muted-foreground/70 hover:text-foreground" title="Move down" data-testid={`level-down-${li}`}><ArrowLeft className="w-3 h-3 -rotate-90" /></button>}
                        <button onClick={() => removeLevel(li)} className="text-muted-foreground/70 hover:text-destructive" title="Remove level" data-testid={`level-remove-${li}`}><X className="w-3 h-3" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {hierarchyLevels.length > 0 && (
            <div className="p-2 rounded-lg bg-muted/20 border border-border/30">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Drill Path Preview</p>
              <div className="flex items-center gap-1 flex-wrap">
                {hierarchyLevels.map((l, i) => {
                  const a = (selectedEntityData?.attributes || []).find(at => at.name === l);
                  return (
                    <React.Fragment key={l}>
                      {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground/30" />}
                      <span className="text-[10px] text-foreground">{a?.business_name || a?.display_name || l}</span>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
          <Button
            onClick={save}
            disabled={!hierarchyName.trim() || hierarchyLevels.length === 0}
            className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95"
            data-testid="save-hierarchy-btn"
          >
            {editHierarchyIdx !== null ? 'Update Hierarchy' : 'Create Hierarchy'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
